/**
 * The side-nav of the settings tab.
 *
 * The artboard draws six items but only three panels. Rather than a nav
 * whose bottom half goes nowhere, every item here has a section of its own:
 * "Protokoll und Kosten" gets the cost summary inline with the full log a
 * click away, "API-Zugang" the template's upload endpoint, "Archivieren" the
 * action the header's overflow menu also carries.
 *
 * The nav is **anchor links**, not buttons that scroll (which is what the
 * Flutter tab does). An `<a href="#…">` is linkable, survives a reload,
 * moves the keyboard focus to the section it names, works when the scroll
 * container is not the window, and needs no scroll listener to stay honest.
 * The cost is that the "you are here" mark follows the hash rather than the
 * scroll position — which is the more truthful of the two anyway: it says
 * where you jumped, not where a rounding error put the viewport.
 */
export interface SettingsSection {
  id: string
  label: string
  icon: string
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  { id: 'auswertung', label: 'Auswertung', icon: 'tune' },
  { id: 'ki-zweitmeinung', label: 'KI-Zweitmeinung', icon: 'auto_awesome' },
  { id: 'datensatz-schema', label: 'Datensatz-Schema', icon: 'table_chart' },
  { id: 'api-zugang', label: 'API-Zugang', icon: 'key' },
  { id: 'protokoll', label: 'Protokoll und Kosten', icon: 'receipt_long' },
  { id: 'archivieren', label: 'Archivieren', icon: 'archive' },
]

/** The DOM id of a section, namespaced so it cannot collide with an id the
 *  shell or another tab puts on the page. */
export function sectionDomId(id: string): string {
  return `einstellungen-${id}`
}

/**
 * Which section a URL hash names, or null for a hash that names none.
 *
 * Null rather than "the first one": marking a section current because the
 * address bar holds someone else's anchor would be a confident wrong answer,
 * and the nav reads fine with nothing marked.
 */
export function sectionFromHash(hash: string): string | null {
  const bare = hash.replace(/^#/, '')
  if (bare === '') return null
  const id = bare.startsWith('einstellungen-') ? bare.slice('einstellungen-'.length) : bare
  return SETTINGS_SECTIONS.some((section) => section.id === id) ? id : null
}
